import { defineStore } from 'pinia';
import {
  AddressResponse,
  EmergencyContactResponse,
  infoPagesTitles,
  PhoneResponse,
} from '@shared/util';
import { ref } from 'vue';
import {
  ISOStringToDateInput,
  formatDateInputValue,
} from '@/lib/format-time-date-input-values';
import { OngoingBookingPricing, useCustomerStore } from './customer';
import {
  BookingType,
  BookingStatus,
  BookingResponse,
  DrivingType,
} from '@shared/bookings';
import {
  PriceBookingDateResponse,
  PriceBookingResponse,
  PricePartyBookingResponse,
} from '@shared/pricing';
import {
  SitterResponse,
  SitterStatus,
  SitterStatusLogItemResponse,
} from '@shared/sitters';
import { UserResponse, UserType } from '@shared/users';
import { ContactUsResponse, FAQResponse, InfoPage } from '@shared/franchises';
import { CustomerResponse } from '@shared/customers';
import {
  ChildResponse,
  NdisFundingAllocationType,
  ParentResponse,
} from '@shared/parents';
import {
  EventBookingResponse,
  EventBookingType,
  eventBookingTypeTitles,
} from '@shared/event-bookings';
import {
  ArtistType,
  EventType,
  PartyBookingResponse,
} from '@shared/party-bookings';
import { OngoingBookingResponse } from '@shared/ongoing-bookings';
import { ResourceResponse } from '@shared/resources';
import { BookingGroupItemResponse } from '@shared/bookings';

// a base class for all forms, which has an optional _validationErrors property
export class Form {
  public _validationErrors?: Record<string, string>; // a map of field names to arrays of error messages
  public _generalError?: string;

  constructor() {
    this._validationErrors = {};
    this._generalError = '';
  }
}

export class LoginForm extends Form {
  email?: string;
  password?: string;
  rememberMe?: boolean;
  messagingToken?: string;
}

export class ResetPasswordForm extends Form {
  password?: string;
  passwordConfirmation?: string;
  token?: string;
}

export class SitterStatusForm extends Form {
  status: SitterStatus;
  comment: string;
  documentsUpdated: boolean;
  constructor(status?: SitterStatus) {
    super();
    if (status) {
      this.status = status;
    }
    this.documentsUpdated = false;
  }
}

// Working on the document
export class SitterDocumentsForm extends Form {
  blueCard: SitterStatus;
  comment: string;
  constructor() {
    super();
  }
}

export class ContactUsForm extends Form {
  email?: string;
  phone?: PhoneForm;
  officeHours?: string;
  address?: AddressForm;
  emergencyHours?: string;

  constructor(contactUs?: ContactUsResponse) {
    super();
    if (contactUs) {
      this.email = contactUs.email;
      this.phone = new PhoneForm(contactUs.phone);
      this.officeHours = contactUs.officeHours;
      this.address = new AddressForm(contactUs.address);
      this.emergencyHours = contactUs.emergencyHours;
    } else {
      this.phone = new PhoneForm();
    }
  }
}

export class FAQForm extends Form {
  _id?: string;
  question?: string;
  answer?: string;
  userType?: UserType;
  modifiedBy?: UserResponse | null;
  lastModified?: string;

  constructor(value: FAQResponse | UserType = 'Customer') {
    super();
    if (typeof value == 'string') {
      this.question = '';
      this.answer = '';
      this.userType = value;
      this.modifiedBy = null;
      this.lastModified = '';
    } else if (value) {
      Object.assign(this, value);
      this.lastModified = formatDateInputValue(value.updatedAt);
    }
  }
}

export class InfoPageForm extends Form {
  content?: string;
  modifiedBy?: UserResponse | null;
  lastModified?: string;
  tag?: string;
  title?: string;

  constructor(infoPageOrTag: InfoPage | string = '') {
    super();
    if (typeof infoPageOrTag == 'string') {
      this.content = '';
      this.modifiedBy = null;
      this.lastModified = '';
      this.tag = infoPageOrTag;
      this.title = infoPagesTitles[infoPageOrTag];
    } else if (infoPageOrTag) {
      const infoPage = infoPageOrTag as InfoPage;
      this.content = infoPage.content;
      this.modifiedBy = infoPage.modifiedBy;
      this.lastModified = formatDateInputValue(infoPage.updatedAt);
      this.tag = infoPage.tag;
      this.title = infoPage.title;
    }
  }
}

export class ResourceForm extends Form {
  _id?: string;
  parentResourceId?: string;
  title: string;
  url?: string;
  group: boolean;
  resources: ResourceResponse[];

  constructor(resourceResponse?: ResourceResponse) {
    super();
    if (resourceResponse) {
      this._id = resourceResponse._id;
      this.title = resourceResponse.title;
      this.url = resourceResponse.url;
      this.group = !resourceResponse.url;
      this.resources = resourceResponse.resources;
    } else {
      this.group = false;
      this.title = '';
      this.url = '';
    }
  }
}

export class SitterForm extends Form {
  _id?: string;
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  phone: PhoneForm | undefined;
  status: SitterStatus;
  sitterStatusLog: SitterStatusLogItemResponse[];
  subscribe?: boolean; // default true
  homeAddress: AddressForm;
  password: string | undefined;
  passwordConfirmation: string | undefined;
  emergencyContact: EmergencyContactForm;
  dateOfBirth: string | undefined;
  registered_for_gst?: boolean;

  constructor(user?: UserResponse) {
    super();
    if (user && user.userType == 'Sitter') {
      // Merge the properties of the 'user' object and its 'organisation' property into the current class
      Object.assign(this, user);
      Object.assign(this, user.organisation);
      this.emergencyContact = new EmergencyContactForm(
        user.organisation.emergencyContact,
      );
      this.homeAddress = new AddressForm(user.organisation.homeAddress);
      this.phone = new PhoneForm(user.organisation.phone);
    } else {
      this.homeAddress = new AddressForm();
      this.emergencyContact = new EmergencyContactForm();
      this.phone = new PhoneForm();
      this.registered_for_gst = false;
    }
  }
}

export class SitterQuestionsForm extends Form {
  experience: string | undefined;
  love: string | undefined;
  spareTime: string | undefined;
  secretTalents: string | undefined;
  favouriteActivity: string | undefined;
  superhero: string | undefined;

  constructor(sitter?: SitterResponse) {
    super();
    if (sitter) {
      this.experience = sitter.experience;
      this.love = sitter.love;
      this.spareTime = sitter.spareTime;
      this.secretTalents = sitter.secretTalents;
      this.favouriteActivity = sitter.favouriteActivity;
      this.superhero = sitter.superhero;
    }
  }
}

// Custom type for address source
// This is a copy of the important parts
// of the Google Maps Places result.
interface AddressComponent {
  short_name: string;
  types: string[];
}

interface AddressSource {
  description?: string;
  address_components?: AddressComponent[];
  place_id?: string;
  geometry?: {
    location?: {
      lat: number;
      lng: number;
    };
  };
}

export class AddressForm extends Form {
  description?: string;
  unitNumber?: string;
  streetNumber?: string;
  street?: string;
  city?: string;
  state?: string;
  postcode?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  isPopulated: boolean;

  constructor(sourceAddress?: AddressSource | AddressResponse) {
    super();
    if (sourceAddress && !('address_components' in sourceAddress)) {
      const localSourceAddress = sourceAddress as AddressResponse;
      this.unitNumber = localSourceAddress.unitNumber ?? undefined;
      this.streetNumber = localSourceAddress.streetNumber;
      this.street = localSourceAddress.street;
      this.city = localSourceAddress.city;
      this.state = localSourceAddress.state;
      this.postcode = localSourceAddress.postcode;
      this.latitude = localSourceAddress.latitude ?? undefined;
      this.longitude = localSourceAddress.longitude ?? undefined;
      this.placeId = localSourceAddress.placeId ?? undefined;
    } else if (sourceAddress) {
      this.description = sourceAddress.description ?? undefined;
      const unitNumber = sourceAddress.address_components?.find((component) =>
        component.types.includes('subpremise'),
      )?.short_name;
      const streetNumber = sourceAddress.address_components?.find((component) =>
        component.types.includes('street_number'),
      )?.short_name;
      const street = sourceAddress.address_components?.find((component) =>
        component.types.includes('route'),
      )?.short_name;

      this.placeId = sourceAddress.place_id;
      this.unitNumber = unitNumber ?? '';
      this.streetNumber = streetNumber ?? '';
      this.street = street ?? '';
      this.city =
        sourceAddress.address_components?.find((component) =>
          component.types.includes('locality'),
        )?.short_name ?? '';
      this.state =
        sourceAddress.address_components?.find((component) =>
          component.types.includes('administrative_area_level_1'),
        )?.short_name ?? '';
      this.postcode =
        sourceAddress.address_components?.find((component) =>
          component.types.includes('postal_code'),
        )?.short_name ?? '';
      this.latitude = sourceAddress.geometry?.location?.lat;
      this.longitude = sourceAddress.geometry?.location?.lng;
    }
    this.isPopulated = !!sourceAddress;
  }
}

export class EmergencyContactForm extends Form {
  name?: string;
  phone?: PhoneForm;
  relationship?: string;
  constructor(emergencyContact?: EmergencyContactResponse) {
    super();
    if (emergencyContact) {
      Object.assign(this, emergencyContact);
      this.phone = new PhoneForm(emergencyContact.phone);
    } else {
      this.phone = new PhoneForm();
    }
  }
}

export class CasualBookingDateForm extends Form {
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  children: Array<string | number>;

  constructor(bookingGroupItem?: BookingGroupItemResponse) {
    super();
    if (bookingGroupItem) {
      this.startDate = bookingGroupItem.jobLocationInterval.startDate.substring(
        0,
        10,
      );
      this.endDate = bookingGroupItem.jobLocationInterval.endDate.substring(
        0,
        10,
      );
      this.startTime = bookingGroupItem.jobLocationInterval.startTime;
      this.endTime = bookingGroupItem.jobLocationInterval.endTime;
      this.children = bookingGroupItem.children.map((child) => child._id); // children is a string array
    } else {
      this.children = [];
    }
  }
}

export class UpdateBookingForm extends CasualBookingDateForm {
  address?: AddressForm;
  parent?: ParentResponse;
  addressNotes?: string;
  updateReason?: string;
  constructor(booking?: BookingResponse) {
    super();
    if (booking) {
      const updatedChildren =
        booking.updatedChildren && booking.updatedChildren.length > 0
          ? booking.updatedChildren.map((child) => child._id)
          : booking.children.map((child) => child._id);
      const updatedAddressNotes = booking.updatedAddressNotes;
      const updatedAddress = booking.updatedAddress;

      /* NOTE: If we to use the updated values, we will need to re-implement the code below.
      FURHTER NOTE: Be sure to convert the updatedInterval to the job timezone (not the current
        user).
        
      const updatedInterval = booking.updatedInterval;
      const start = updatedInterval
        ? updatedInterval.start
        : booking.interval.start;
      const end = updatedInterval ? updatedInterval.end : booking.interval.end;
      const startDate = format(new Date(start), 'yyyy-MM-dd');
      const endDate = format(new Date(end), 'yyyy-MM-dd');

      const startTime = format(new Date(start), 'HH:mm:ss');
      const endTime = format(new Date(end), 'HH:mm:ss');
      */
      // format the date and time
      this.startDate = booking.jobLocationInterval.startDate;
      this.endDate = booking.jobLocationInterval.endDate;
      this.startTime = booking.jobLocationInterval.startTime;
      this.endTime = booking.jobLocationInterval.endTime;
      this.children = updatedChildren;
      this.parent = booking.parent;
      this.addressNotes = updatedAddressNotes ?? booking.addressNotes;

      this.address = new AddressForm(updatedAddress ?? booking.address);
      this.updateReason = booking.updateReason || '';
    }
  }
}

export class ConfirmUpdateBookingForm extends Form {
  accept?: boolean;
  bookingId?: string;
  message?: string | undefined;

  constructor(accept: boolean, bookingId: string, message?: string) {
    super();
    this.accept = accept;
    this.bookingId = bookingId;
    this.message = accept ? undefined : message;
  }
}

export class CancelBookingForm extends Form {
  refundBookingCredit: boolean;
  waiveCancellationFee: boolean;
  notifyCustomer: boolean;
  reasonForCancellation: string;
  bookingId: string;
  markAsUnfilled: boolean;
}

export class CasualBookingForm extends Form {
  _id?: string;

  parent?: string;

  // freeParkingAvailable?: boolean;
  // parkingDetails?: string;

  bookingDates: Array<CasualBookingDateForm>;

  useHomeAddress?: boolean;
  address: AddressForm;
  addressNotes?: string;

  favouritesFirst?: boolean;
  requestedSitters?: Array<string>;

  allowFallbackSitters?: boolean;

  drivingIsRequired?: boolean;
  vehicleType?: DrivingType;
  drivingDetails?: string;

  reason?: string;

  otherDetails?: string;

  termsAgreed: boolean;
  packSelection: number;

  useSavedPaymentMethod: boolean;

  // retrieved from the server when
  // the booking is priced.
  pricing?: PriceBookingResponse;

  // returned from the server during payment process.
  // clientSecret?: string;
  // paymentIntentId?: string;
  // totalBookingFeeCharged?: string;
  // paymentRequired?: boolean;

  bookingFeePaymentType?: string;
  xeroInvoiceUrl?: string;

  constructor(sourceBooking?: BookingResponse) {
    super();
    if (sourceBooking) {
      // Use only the first casual booking dates
      Object.assign(this, sourceBooking);

      this.bookingDates = sourceBooking.bookingGroup.map(
        (bookingGroupItem) => new CasualBookingDateForm(bookingGroupItem),
      );

      this.address = new AddressForm(sourceBooking.address);
      this.drivingIsRequired = sourceBooking.drivingDetails !== '';
      // this.freeParkingAvailable = !sourceBooking.parkingDetails;
      this.favouritesFirst = sourceBooking.requestedSitters.length > 0;
      this.useHomeAddress = false;
      this.parent = sourceBooking.parent._id;
    } else {
      this.address = new AddressForm();
      this.bookingDates = [new CasualBookingDateForm()];
      this.allowFallbackSitters = true;
    }
    this.termsAgreed = false;
    this.packSelection = 0;
    this.useSavedPaymentMethod = false;
  }
}

export class ManualBookingForm extends Form {
  _id?: string;

  parent?: string;

  // freeParkingAvailable?: boolean;
  // parkingDetails?: string;

  jobLocationInterval: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };

  children: Array<string>;

  address: AddressForm;
  addressNotes?: string;

  favouritesFirst?: boolean;
  requestedSitters?: Array<string | number>;
  requestedSitterName?: string;
  allowFallbackSitters?: boolean;

  drivingIsRequired?: boolean;
  vehicleType?: DrivingType;
  drivingDetails?: string;

  reason?: string;

  otherDetails?: string;

  status?: BookingStatus;

  bookingType: BookingType;

  // returned from the server during payment process.
  clientSecret?: string;
  paymentIntentId?: string;
  totalBookingFeeCharged?: string;
  paymentRequired?: boolean;
  bookingFeePaymentType?: string;
  xeroInvoiceUrl?: string;

  useSavedPaymentMethod: boolean;

  constructor() {
    super();
    this.address = new AddressForm();
    this.jobLocationInterval = {};
    this.children = [];
    this.bookingType = 'CasualBooking';
    this.useSavedPaymentMethod = false;
  }
}

export class PartyBookingForm extends Form {
  _id?: string | number;

  parent?: string;

  artistTypes: Array<ArtistType>;
  eventContactName?: string;
  eventContactPhone?: PhoneForm;

  birthdayAge?: string; // for birthday parties
  eventType?: EventType; // for party bookings
  eventName?: string; // for party bookings

  // freeParkingAvailable?: boolean;
  // parkingDetails?: string;

  dateOfBooking?: string;
  startTime?: string;
  endTime?: string;
  numChildren?: string;

  address?: AddressForm;
  addressNotes?: string;

  locationOutdoors?: boolean;
  weatherPlan?: string;

  otherDetails?: string;

  useSavedPaymentMethod?: boolean;

  /** Pricing response from validateBooking; set by form view before transition to confirmation */
  pricing?: PricePartyBookingResponse;

  constructor(partyBookingResponse?: PartyBookingResponse) {
    super();
    if (partyBookingResponse) {
      const {parent, address, eventContactPhone, ...rest} = partyBookingResponse;
      this.parent = parent._id;
      this.address = new AddressForm(address);
      this.eventContactPhone = new PhoneForm(eventContactPhone);
      Object.assign(this, rest);
    } else {
      this.artistTypes = [];
      this.eventContactPhone = new PhoneForm();
      this.useSavedPaymentMethod = true;
    }
  }
}

// a function to check if a booking is an invoice booking.  An invoice booking is
// one where any of the children in the BookingForm.booking_dates have invoice_required
// set to true, or api.user.role is company.
function isInvoiceBooking(booking: CasualBookingForm) {
  const customerStore = useCustomerStore();

  if (customerStore.isCompany) {
    return true;
  }

  const parent = customerStore.parents.find(
    (parent: ParentResponse) => parent._id == booking.parent,
  );
  if (!parent) {
    return false;
  }

  booking.bookingDates.forEach((bookingDate) => {
    if (
      bookingDate.children.some(
        (childId) =>
          parent.children.find((child: ChildResponse) => child._id == childId)
            ?.ndisFundingAllocation == 'selfManaged',
      )
    ) {
      return true;
    }
  });

  return false;
}

export class BookingRuleForm extends Form {
  vueId: number;
  startDate?: string;
  startTime?: string;
  endTime?: string;
  endDate?: string;
  frequency: number;
  children: Array<string | number>;

  constructor() {
    super();
    this.frequency = 7;
    this.children = [];
    this.vueId = new Date().getTime();
  }
}

export class OngoingBookingForm extends Form {
  id?: string | number;
  parent?: string | number;

  bookingRules: Array<BookingRuleForm>;

  isFlexible?: boolean;
  atHomeAddress?: boolean;
  address?: AddressForm;
  addressNotes?: string;

  // freeParkingAvailable?: number | boolean;
  // parkingDetails?: string;
  drivingIsRequired?: boolean;
  vehicleType?: string;
  parentAtHome?: boolean;
  aTypicalDay?: string;

  clientSecret?: string;
  paymentIntentId?: string;

  paymentRequired?: boolean;

  useSavedPaymentMethod: boolean;

  // retrieved from the server when
  // the booking is priced.
  pricing?:
    | OngoingBookingPricing
    | {
        pricingType: 'manual';
        manualHandlingRequired: true;
        customerMessage: string;
        adminMessage: string;
      };

  constructor(ongoingBookingResponse?: OngoingBookingResponse) {
    super();
    if (ongoingBookingResponse) {
      this.id = ongoingBookingResponse._id;
      this.parent = ongoingBookingResponse.parent._id;
      this.bookingRules = ongoingBookingResponse.bookingDateTime.map(
        (rule, idx) => {
          return {
            ...rule,
            vueId: idx,
          };
        },
      );
      this.isFlexible = ongoingBookingResponse.isFlexible;
      this.atHomeAddress = false;
      this.address = new AddressForm(ongoingBookingResponse.address);
      this.addressNotes = ongoingBookingResponse.addressNotes;
      // this.parkingDetails = ongoingBookingResponse.parkingDetails;
      // if (this.parkingDetails) {
      //   this.freeParkingAvailable = false;
      // } else {
      //   this.freeParkingAvailable = true;
      // }
      this.drivingIsRequired = ongoingBookingResponse.vehicleType !== '';
      this.vehicleType = ongoingBookingResponse.vehicleType;
      this.parentAtHome = ongoingBookingResponse.parentAtHome;
      this.aTypicalDay = ongoingBookingResponse.aTypicalDay;
      this.useSavedPaymentMethod = false;
      this.pricing = undefined;
    } else {
      this.bookingRules = [new BookingRuleForm()];
      this.useSavedPaymentMethod = false;
    }
  }
}

export class EventBookingDateForm extends Form {
  date?: string;
  startTime?: string;
  endTime?: string;
  numChildren?: string;
}

export class EventBookingForm extends Form {
  _id?: string | number;
  eventType?: EventBookingType;
  eventTypeChoices = eventBookingTypeTitles;
  eventTypeDetails?: string;
  eventContactName?: string;
  eventContactPhone?: PhoneForm;
  eventContactEmail?: string;
  weddingCoordinatorName?: string;
  weddingCoordinatorPhone?: PhoneForm;
  bookingDates: Array<EventBookingDateForm>;
  address?: AddressForm;
  addressNotes?: string;
  childrenAges?: string;
  childrenOutOfNappies?: 0 | 1 | boolean;

  // freeParkingAvailable?: boolean;
  // parkingDetails?: string;
  otherDetails?: string;

  constructor(eventBookingResponse?: EventBookingResponse) {
    super();
    if (eventBookingResponse) {
      Object.assign(this, eventBookingResponse);
      this.address = new AddressForm(eventBookingResponse.address);
      this.bookingDates = eventBookingResponse.bookingDateTime.map(
        (date) => date,
      );
      if (typeof this.childrenOutOfNappies === 'number') {
        this.childrenOutOfNappies = this.childrenOutOfNappies === 1;
      }
      this.eventContactPhone = new PhoneForm(
        eventBookingResponse.eventContactPhone,
      );
      if (eventBookingResponse.eventType == 'wedding') {
        this.weddingCoordinatorPhone = new PhoneForm(
          eventBookingResponse.weddingCoordinatorPhone,
        );
      }
    } else {
      this.bookingDates = [new EventBookingDateForm()];
      this.eventContactPhone = new PhoneForm();
      this.weddingCoordinatorPhone = new PhoneForm();
    }
  }
}

export class PublishBookingPricingForm extends Form {
  title: string;
  unit: string;
  quantity: string;
  customerCharge: string;
  sitterPayment: string;

  constructor() {
    super();
    this.title = '';
    this.unit = '';
    this.quantity = '';
    this.customerCharge = '';
    this.sitterPayment = '';
  }
}

// this form is used within wedding and event booking
// carer forms (CarerBookingForm) to hold a single
// day of the wedding/event.
export class DayOfBookingForm extends Form {
  bookingDateId: string;
  eventDatePricings: Array<PublishBookingPricingForm>;
  travelFeeIncluded: boolean;
  constructor(bookingDateId?: string) {
    super();
    if (bookingDateId) {
      this.bookingDateId = bookingDateId;
    }
    this.eventDatePricings = [new PublishBookingPricingForm()];
    this.travelFeeIncluded = false;
  }
}

export class CarerBookingForm extends Form {
  tags: string[];
  role?: string;
  selectedSitterId?: string;
  daysOfEvent: DayOfBookingForm[];
  constructor(daysOfEvent?: string[]) {
    super();
    this.tags = [];
    this.role = undefined;
    this.selectedSitterId = undefined;
    if (daysOfEvent) {
      this.daysOfEvent = daysOfEvent.map((day) => new DayOfBookingForm(day));
    } else {
      this.daysOfEvent = [new DayOfBookingForm()];
    }
  }
}

export class PublishEventBookingForm extends Form {
  bookingId: string;
  eventBookingCarers: Array<CarerBookingForm>;
  parentId?: string;
  constructor(
    bookingId?: string,
    carers?: CarerBookingForm[],
    parentId?: string,
  ) {
    super();
    if (bookingId && carers) {
      this.bookingId = bookingId;
      this.eventBookingCarers = carers;
      this.parentId = parentId || '';
    } else {
      this.eventBookingCarers = [new CarerBookingForm()];
    }
  }
}

export class ChildForm extends Form {
  _id?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  hasMedicalConditions?: boolean;
  medicalConditions?: string;
  hasSpecialNeeds?: boolean;
  specialNeeds?: string;
  isNdisParticipant?: boolean;
  ndisNumber?: string;
  ndisFundingAllocation?: NdisFundingAllocationType;
  ndisInvoiceEmail?: string;
  otherDetails?: string;

  constructor(child?: ChildResponse) {
    super();
    if (child) {
      this._id = child._id;
      this.firstName = child.firstName;
      this.lastName = child.lastName;
      this.dateOfBirth = ISOStringToDateInput(child.dateOfBirth);
      this.hasMedicalConditions = child.medicalConditions != '';
      this.medicalConditions = child.medicalConditions;
      this.hasSpecialNeeds = child.specialNeeds != '';
      this.specialNeeds = child.specialNeeds;
      this.isNdisParticipant = child.ndisNumber != '';
      this.ndisNumber = child.ndisNumber;
      this.ndisFundingAllocation = child.ndisFundingAllocation;
      this.ndisInvoiceEmail = child.ndisInvoiceEmail;
      this.otherDetails = child.otherDetails;
    }
  }
}

export class ParentForm extends Form {
  _id?: string | number;
  customer: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: PhoneForm;
  homeAddress: AddressForm;
  homeAddressNotes?: string;
  emergencyContact: EmergencyContactForm;
  hasPets?: boolean;
  petDetails?: string;
  subscribe?: boolean; // used only for private parent profile update.

  constructor(
    customer?: CustomerResponse,
    parent?: ParentResponse,
    user?: UserResponse,
  ) {
    super();
    this.homeAddress = new AddressForm();
    this.emergencyContact = new EmergencyContactForm();
    this.customer = customer?._id || '';

    this.subscribe = user?.subscribe;

    if (parent) {
      Object.assign(this, parent);
      this.phone = new PhoneForm(parent.phone);
      this.homeAddress = new AddressForm(parent.homeAddress);
      this.emergencyContact = new EmergencyContactForm(parent.emergencyContact);
      this.hasPets = parent.petDetails != '';
      this.petDetails = parent.petDetails;
    } else if (customer && !customer.isCompany && user) {
      // handle private customer with incomplete profile
      this.firstName = user.firstName;
      this.lastName = user.lastName;
      this.email = user.email;
      this.phone = new PhoneForm(customer.phone);
    } else {
      this.phone = new PhoneForm();
    }
  }
}

export class PhoneForm extends Form {
  number?: string;
  iso2?: string;
  dialCode?: string;

  constructor(phone?: PhoneResponse) {
    super();
    if (phone) {
      this.number = phone.number;
      this.iso2 = phone.iso2;
      this.dialCode = phone.dialCode;
    } else {
      this.number = '';
      this.iso2 = 'AU';
      this.dialCode = '61';
    }
  }
}

export class CompanyForm extends Form {
  _id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: PhoneForm;
  companyName?: string;
  abn?: string;
  subscribe?: boolean;

  constructor(company?: CustomerResponse, user?: UserResponse) {
    super();
    this.companyName = company?.companyName;
    this._id = company?._id;
    this.abn = company?.abn;
    if (company) {
      this.phone = new PhoneForm(company.phone);
    }

    // subcribe handling
    this.email = user?.email;
    this.firstName = user?.firstName;
    this.lastName = user?.lastName;
    this.subscribe = user?.subscribe;
  }
}

export class ChangePasswordForm extends Form {
  oldPassword?: string;
  password?: string;
  passwordConfirmation?: string;
}

export class CustomerForm extends Form {
  // all customer types
  _id?: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: PhoneForm;
  subscribe?: boolean;
  isCompany: boolean;
  isNotForProfit: boolean;
  // Details for only Parents
  parent: ParentForm;

  // Details for company only
  companyName?: string;
  abn?: string;

  constructor(
    customer?: CustomerResponse,
    user?: UserResponse,
    parent?: ParentResponse,
  ) {
    super();
    this.parent = new ParentForm();
    this.isCompany = false;

    if (customer) {
      if (!user) {
        throw new Error('If you specify a client, then a user is required');
      }
    } else {
      return;
    }

    customer = customer as CustomerResponse;
    user = user as UserResponse;
    this.isCompany = customer?.isCompany;
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.email = user.email;
    this.subscribe = user.subscribe;
    this.phone = new PhoneForm();

    if (!customer.isCompany) {
      if (!parent) {
        throw new Error('Parent is required for non-company clients');
      }
      this.parent = new ParentForm(customer, parent);
    } else {
      this.companyName = customer.companyName;
      this.abn = customer.abn;
      this.phone = new PhoneForm(customer.phone);
      this.isNotForProfit = customer.isNotForProfit;
    }
  }
}

export class CompleteBookingForm extends Form {
  bookingId?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  numChildren?: string; // because this will be filled by an input, it is a string.
  pricing?: PriceBookingDateResponse;
  confirm: boolean;
  bookingType?: BookingType;
  sitterAdditionalExpense?: string; // used to store the additional expense for the sitter
  additionalExpensesDescription?: string; // used to store the description of additional expenses

  constructor(booking?: BookingResponse) {
    super();
    this.confirm = false;
    if (booking) {
      this.bookingId = booking._id;
      const completion = booking.completionDetails;
      const interval = completion ?? booking.jobLocationInterval;

      this.startDate = interval?.startDate;
      this.endDate = interval?.endDate;
      this.startTime = interval?.startTime;
      this.endTime = interval?.endTime;
      this.numChildren = (completion?.numChildren ?? booking.children.length).toString();
      
      this.bookingType = booking.bookingType;

      this.sitterAdditionalExpense = completion?.sitterAdditionalExpense;
      this.additionalExpensesDescription = completion?.additionalExpensesDescription;
    }
  }
}

export const useFormStore = defineStore('forms', () => {
  const casualBookingForm = ref(new CasualBookingForm());
  const partyBookingForm = ref(new PartyBookingForm());

  const ongoingBookingForm = ref(new OngoingBookingForm());

  const eventBookingForm = ref(new EventBookingForm());

  const manualBookingForm = ref(new ManualBookingForm());

  const completeBookingForm = ref(new CompleteBookingForm());

  return {
    casualBookingForm,
    partyBookingForm,
    ongoingBookingForm,
    eventBookingForm,
    manualBookingForm,
    completeBookingForm,
    isInvoiceBooking,
  };
});
